import { useState } from "react";
import { Menu, MenuButton, MenuItems } from "@headlessui/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EllipsisHorizontalIcon,
} from "@heroicons/react/20/solid";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const getColor = (eventType) => {
  switch (eventType) {
    case "activity":
      return "bg-purple-100 text-purple-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

const getInitials = (displayName) => {
  if (!displayName) return "NA";
  const names = displayName.trim().split(/\s+/);
  if (names.length === 0) return "NA";
  const first = names[0][0] || "";
  const last = names[names.length - 1][0] || "";
  return `${first}${last}`.toUpperCase();
};

const formatDate = (date) =>
  new Date(date).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

export default function ProspectsCalendar({
  prospects,
  navigate,
  visibleEventTypes,
  users,
}) {
  const [currentDate, setCurrentDate] = useState(new Date());

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];

    const startDay = firstDay.getDay();
    for (let i = startDay; i > 0; i--) {
      const prevDay = new Date(year, month, 1 - i);
      days.push({
        date: prevDay.toISOString().split("T")[0],
        isCurrentMonth: false,
        events: [],
      });
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      const day = new Date(year, month, i);
      const dayString = day.toISOString().split("T")[0];
      const events = [];

      if (visibleEventTypes.activity) {
        prospects.forEach((prospect) => {
          (prospect.activities || []).forEach((activity) => {
            if (
              activity.dueDate &&
              new Date(activity.dueDate).toISOString().split("T")[0] ===
                dayString
            ) {
              const assignedUser = users.find(
                (user) =>
                  user._id === (activity.assignedTo?._id || activity.assignedTo)
              );
              events.push({
                id: `${prospect._id}-${activity._id}`,
                name: activity.title,
                prospectName: prospect.projectName,
                userId:
                  activity.assignedTo?._id ||
                  activity.createdBy?._id ||
                  activity.assignedTo,
                userInitials: getInitials(
                  assignedUser?.displayName ||
                    activity.assignedTo?.displayName ||
                    activity.createdBy?.displayName
                ),
                userColor: getColor("activity"),
                eventType: "activity",
                dueDate: activity.dueDate,
                activityId: activity._id,
                href: `/Prospects/${prospect._id}?tab=Activities&activityId=${activity._id}`,
              });
            }
          });
        });
      }

      days.push({
        date: dayString,
        isCurrentMonth: true,
        isToday:
          Date.UTC(day.getFullYear(), day.getMonth(), day.getDate()) ===
          Date.UTC(
            new Date().getFullYear(),
            new Date().getMonth(),
            new Date().getDate()
          ),
        events,
      });
    }

    const endDay = lastDay.getDay();
    for (let i = 1; i < 7 - endDay; i++) {
      const nextDay = new Date(year, month + 1, i);
      days.push({
        date: nextDay.toISOString().split("T")[0],
        isCurrentMonth: false,
        events: [],
      });
    }

    return days;
  };

  const days = getDaysInMonth(currentDate);
  const selectedDay = days.find((day) => day.isToday) || days[0];

  const handlePreviousMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
    );
  };

  const handleNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    );
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  return (
    <div className="lg:flex lg:h-full lg:flex-col mx-auto">
      <header className="flex items-center justify-between border-b border-gray-700 px-4 py-4 bg-gray-800 lg:flex-none">
        <div className="flex items-center space-x-4">
          <h1 className="text-base font-semibold text-white">
            <time dateTime={currentDate.toISOString().split("T")[0]}>
              {currentDate.toLocaleString("default", {
                month: "long",
                year: "numeric",
              })}
            </time>
          </h1>
        </div>
        <div className="flex items-center">
          <div className="relative flex items-center rounded-md bg-gray-700 shadow-sm md:items-stretch">
            <button
              type="button"
              onClick={handlePreviousMonth}
              className="flex h-9 w-12 items-center justify-center rounded-l-md border-y border-l border-gray-600 pr-1 text-gray-300 hover:text-gray-200 focus:relative md:w-9 md:pr-0 md:hover:bg-gray-600"
            >
              <span className="sr-only">Previous month</span>
              <ChevronLeftIcon className="size-5" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={handleToday}
              className="hidden border-y border-gray-600 px-3.5 text-sm font-semibold text-gray-200 hover:bg-gray-600 focus:relative md:block"
            >
              Month
            </button>
            <span className="relative -mx-px h-5 w-px bg-gray-600 md:hidden" />
            <button
              type="button"
              onClick={handleNextMonth}
              className="flex h-9 w-12 items-center justify-center rounded-r-md border-y border-r border-gray-600 pl-1 text-gray-300 hover:text-gray-200 focus:relative md:w-9 md:pl-0 md:hover:bg-gray-600"
            >
              <span className="sr-only">Next month</span>
              <ChevronRightIcon className="size-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <div className="shadow-sm ring-1 ring-black/5 lg:flex lg:flex-auto lg:flex-col">
        <div className="grid grid-cols-7 gap-px border-b border-gray-800 bg-gray-800 text-center text-xs font-semibold text-gray-300 lg:flex-none">
          {["M", "T", "W", "T", "F", "S", "S"].map((day) => (
            <div
              key={day}
              className="bg-gray-700 py-2 border-r border-gray-800"
            >
              {day}
            </div>
          ))}
        </div>
        <div className="flex bg-gray-800 text-xs text-gray-300 lg:flex-auto">
          <div className="hidden w-full lg:grid lg:grid-cols-7 lg:grid-rows-6 lg:gap-x-px lg:gap-y-0.5 lg:border-t lg:border-gray-800">
            {days.map((day, index) => (
              <div
                key={day.date}
                className={classNames(
                  day.isCurrentMonth
                    ? "bg-gray-800"
                    : "bg-gray-700 text-gray-500",
                  "relative px-2 py-2 border-b",
                  index % 7 !== 6 ? "border-r border-gray-600" : "",
                  index < days.length - 7
                    ? "border-b border-gray-600"
                    : "border-b border-gray-600"
                )}
              >
                <time
                  dateTime={day.date}
                  className={classNames(
                    day.isToday
                      ? "flex size-6 items-center justify-center rounded-full bg-orange-400 font-semibold text-white"
                      : "font-medium",
                    "mb-1 text-gray-300"
                  )}
                >
                  {day.date.split("-").pop().replace(/^0/, "")}
                </time>
                {day.events.length > 0 && (
                  <ol className="mt-1 space-y-1">
                    {day.events.slice(0, 2).map((event) => (
                      <li key={`${event.id}-${event.eventType}`}>
                        <button
                          onClick={() => navigate(event.href)}
                          className={classNames(
                            event.userColor,
                            "group flex w-full items-center rounded-full px-2 py-0.5"
                          )}
                        >
                          <svg
                            viewBox="0 0 6 6"
                            aria-hidden="true"
                            className="fill-purple-500 size-1.5 mr-1"
                          >
                            <circle r={3} cx={3} cy={3} />
                          </svg>
                          <p className="flex-auto truncate font-medium text-xs">
                            {event.prospectName}
                          </p>
                          <span className="ml-2 font-medium text-xs">
                            {event.userInitials}
                          </span>
                        </button>
                      </li>
                    ))}
                    {day.events.length > 2 && (
                      <li className="text-gray-400">
                        + {day.events.length - 2} more
                      </li>
                    )}
                  </ol>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
